import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Select,
  Input,
  DatePicker,
  Tag,
  Button,
  Space,
  Row,
  Col,
  Tooltip,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminApi, AuditLogFilters } from '@/api/adminApi';
import { AuditLog } from '@/types/common';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

const { RangePicker } = DatePicker;

const ENTITY_TYPES = ['OPPORTUNITY', 'WORKFLOW', 'BID_DECISION', 'DOCUMENT'];
const ACTIONS = ['CREATED', 'UPDATED', 'SUBMITTED', 'PHASE_ADVANCED'];

const ACTION_COLORS: Record<string, string> = {
  CREATED: 'green',
  UPDATED: 'blue',
  SUBMITTED: 'orange',
  PHASE_ADVANCED: 'purple',
};

const AuditLogViewer = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 0,
    size: 10,
  });

  // Filter input state (applied on Search click)
  const [entityType, setEntityType] = useState<string | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [username, setUsername] = useState('');
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAuditLog(filters);
      setLogs(response.content);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      entityType: entityType || undefined,
      action: action || undefined,
      username: username || undefined,
      startDate: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
      endDate: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
      page: 0,
    }));
  };

  const handleReset = () => {
    setEntityType(undefined);
    setAction(undefined);
    setUsername('');
    setDateRange(null);
    setFilters({ page: 0, size: 10 });
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setFilters((prev) => ({
      ...prev,
      page: (pagination.current || 1) - 1,
      size: pagination.pageSize || 10,
    }));
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) =>
        date ? dayjs(date).format('MMM DD, YYYY HH:mm:ss') : '-',
    },
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: 'Entity Type',
      dataIndex: 'entityType',
      key: 'entityType',
      width: 140,
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (act: string) => (
        <Tag color={ACTION_COLORS[act] || 'default'}>{act}</Tag>
      ),
    },
    {
      title: 'Entity ID',
      dataIndex: 'entityId',
      key: 'entityId',
      width: 140,
      render: (id: string, record: AuditLog) => {
        if (
          record.entityType === 'OPPORTUNITY' ||
          record.entityType === 'WORKFLOW'
        ) {
          const path =
            record.entityType === 'OPPORTUNITY'
              ? `/opportunities/${id}`
              : `/opportunities/${id}`;
          return (
            <a href={path} style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {id ? id.substring(0, 8) + '...' : '-'}
            </a>
          );
        }
        return (
          <Tooltip title={id}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {id ? id.substring(0, 8) + '...' : '-'}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (details: string) => (
        <Tooltip title={details}>
          <span>{details || '-'}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {/* Filter Bar */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Entity Type"
            style={{ width: '100%' }}
            value={entityType}
            onChange={(val) => setEntityType(val || undefined)}
            allowClear
          >
            {ENTITY_TYPES.map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Action"
            style={{ width: '100%' }}
            value={action}
            onChange={(val) => setAction(val || undefined)}
            allowClear
          >
            {ACTIONS.map((act) => (
              <Select.Option key={act} value={act}>
                {act}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Input
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)
            }
          />
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Audit Log Table */}
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: (filters.page || 0) + 1,
          pageSize: filters.size || 10,
          total: totalElements,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} entries`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default AuditLogViewer;
